import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, RefreshCcw, Save, Search, X, CheckCircle2,
  Sparkles, HelpCircle, Circle, Fingerprint,
} from "lucide-react";

import {
  getTournamentSyncPreview,
  commitTournamentSync,
  getMatchSyncPreview,
  commitMatchSync,
} from "../../../api/tournamentSync.api";
import { listPlayers } from "../../../api/coredata.api";
import api from "../../../api/axios";

// scope → (preview-fetch, commit-post, preview-payload key that holds
// the header dict). Wrappers below (CompetitionSync / MatchSync) pick
// the scope; SyncPage stays scope-agnostic below the fold.
const SCOPE_CONFIG = {
  competition: {
    fetchPreview: getTournamentSyncPreview,
    commit: commitTournamentSync,
    headerKey: "competition",
  },
  match: {
    fetchPreview: getMatchSyncPreview,
    commit: commitMatchSync,
    headerKey: "match",
  },
};

const TAG_META = {
  auto_from_ci_id: {
    label: "Cricinfo ID match",
    hint: "Archival row carried the source Cricinfo id — exact match to canonical.",
    color: "bg-indigo-500/15 text-indigo-200 border-indigo-400/40",
    icon: Fingerprint,
    order: 0,
  },
  auto_from_alias: {
    label: "Already known",
    hint: "Mapped from a prior sync — accepted by default.",
    color: "bg-emerald-500/15 text-emerald-200 border-emerald-400/40",
    icon: CheckCircle2,
    order: 1,
  },
  high_confidence: {
    label: "High confidence",
    hint: "Auto-suggested — review before Save.",
    color: "bg-sky-500/15 text-sky-200 border-sky-400/40",
    icon: Sparkles,
    order: 2,
  },
  needs_review: {
    label: "Needs review",
    hint: "Ambiguous — pick the right canonical player.",
    color: "bg-amber-500/15 text-amber-200 border-amber-400/40",
    icon: HelpCircle,
    order: 3,
  },
  no_match: {
    label: "No match",
    hint: "No canonical suggestion — search manually or skip.",
    color: "bg-rose-500/15 text-rose-200 border-rose-400/40",
    icon: Circle,
    order: 4,
  },
};

export default function CompetitionSync() {
  return <SyncPage scope="competition" />;
}

export function MatchSync() {
  return <SyncPage scope="match" />;
}

function SyncPage({ scope }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const cfg = SCOPE_CONFIG[scope];
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [mappings, setMappings] = useState({}); // archival_id -> canonical_id
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [minActivity, setMinActivity] = useState(0);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  useEffect(() => {
    let alive = true;
    setError("");
    cfg.fetchPreview(id)
      .then((r) => {
        if (!alive) return;
        setPreview(r.data);
        // Auto-fill CI-id matches, alias hits, and high-confidence top suggestions.
        const init = {};
        for (const row of r.data.rows || []) {
          if (row.tag === "auto_from_ci_id" || row.tag === "auto_from_alias" || row.tag === "high_confidence") {
            const s = row.suggestions?.[0];
            if (s) init[row.archival.id] = s.id;
          }
        }
        setMappings(init);
        // Collapse the auto-accepted groups by default — nothing to
        // review; operator only opens them if they want to spot-check.
        setCollapsedGroups({ auto_from_ci_id: true, auto_from_alias: true });
      })
      .catch((e) => {
        if (!alive) return;
        const detail =
          e?.response?.data?.detail ||
          (typeof e?.response?.data === "string" ? e.response.data : "") ||
          e?.message ||
          "Couldn't load preview.";
        const httpStatus = e?.response?.status;
        setError(httpStatus ? `${httpStatus} · ${detail}` : detail);
      });
    return () => {
      alive = false;
    };
  }, [id, cfg]);

  const header = preview?.[cfg.headerKey];
  const rows = preview?.rows || [];

  // Apply min-activity filter (in-batch balls faced OR balls bowled).
  const filteredRows = useMemo(() => {
    if (minActivity <= 0) return rows;
    return rows.filter(
      (r) => Math.max(r.archival.balls_faced || 0, r.archival.balls_bowled || 0) >= minActivity
    );
  }, [rows, minActivity]);

  // Group by tag.
  const groups = useMemo(() => {
    const g = { auto_from_ci_id: [], auto_from_alias: [], high_confidence: [], needs_review: [], no_match: [] };
    for (const r of filteredRows) {
      (g[r.tag] || g.no_match).push(r);
    }
    return g;
  }, [filteredRows]);

  const mappedCount = useMemo(
    () => Object.values(mappings).filter((v) => v).length,
    [mappings]
  );

  const setMap = (archivalId, canonicalId) =>
    setMappings((m) => ({ ...m, [archivalId]: canonicalId || "" }));

  const acceptGroup = (tag) => {
    setMappings((prev) => {
      const next = { ...prev };
      for (const row of groups[tag] || []) {
        const s = row.suggestions?.[0];
        if (s && !next[row.archival.id]) next[row.archival.id] = s.id;
      }
      return next;
    });
  };

  const clearAll = () => setMappings({});

  const onSave = async () => {
    const payload = Object.entries(mappings)
      .filter(([, v]) => v)
      .map(([archival_id, canonical_id]) => ({ archival_id, canonical_id }));
    if (payload.length === 0) return;
    setSaving(true);
    setError("");
    try {
      const r = await cfg.commit(id, payload);
      setResult(r.data);
      const fresh = await cfg.fetchPreview(id);
      setPreview(fresh.data);
      setMappings({});
    } catch (e) {
      setError(e?.response?.data?.detail || "Sync failed.");
    } finally {
      setSaving(false);
    }
  };

  const toggleGroup = (tag) =>
    setCollapsedGroups((c) => ({ ...c, [tag]: !c[tag] }));

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md bg-bg-secondary border border-border-default text-text-primary hover:bg-bg-main"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </div>

      <div className="mb-5">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <RefreshCcw className="w-5 h-5 text-button-primary" />
          Sync players to canonical profiles
        </h2>
        {header && scope === "competition" && (
          <p className="text-sm text-text-secondary mt-1">
            <span className="font-medium text-text-primary">{header.name}</span>
            {header.season && <span> · {header.season}</span>}
            {header.match_type_code && (
              <span className="ml-2 inline-block text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border bg-button-primary/15 text-button-primary border-button-primary/30">
                {header.match_type_code}
              </span>
            )}
            {header.source && (
              <span className="ml-2 inline-block text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border bg-bg-secondary text-text-secondary border-border-default">
                source: {header.source}
              </span>
            )}
            <span className="ml-2">
              · {header.match_count} match{header.match_count === 1 ? "" : "es"}
              {" · "}
              {header.archival_player_count} archival player
              {header.archival_player_count === 1 ? "" : "s"}
            </span>
          </p>
        )}
        {header && scope === "match" && (
          <p className="text-sm text-text-secondary mt-1">
            <span className="font-medium text-text-primary">{header.title}</span>
            {header.match_number != null && (
              <span className="ml-2 text-text-secondary font-mono">
                #{header.match_number}
              </span>
            )}
            {header.competition?.name && (
              <span> · {header.competition.name}
                {header.competition.season ? ` ${header.competition.season}` : ""}
              </span>
            )}
            {header.match_date && <span> · {header.match_date}</span>}
            {header.match_type_code && (
              <span className="ml-2 inline-block text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border bg-button-primary/15 text-button-primary border-button-primary/30">
                {header.match_type_code}
              </span>
            )}
            {header.source && (
              <span className="ml-2 inline-block text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border bg-bg-secondary text-text-secondary border-border-default">
                source: {header.source}
              </span>
            )}
            <span className="ml-2">
              · {header.archival_player_count} archival player
              {header.archival_player_count === 1 ? "" : "s"}
            </span>
          </p>
        )}
      </div>

      <div className="rounded-lg border border-border-default bg-bg-primary p-4 mb-4">
        <p className="text-xs text-text-secondary leading-relaxed">
          Rows are grouped by confidence. <b>Already known</b> mappings come
          from prior syncs — accepted by default. <b>High confidence</b>{" "}
          auto-picks the top suggestion; review and Save. Every Save writes
          new alias entries so the next batch auto-resolves the same names.
          Destructive: archival rows are deleted after FK re-pointing — to
          reverse, purge + re-ingest the batch.
        </p>
      </div>

      {error && (
        <div className="rounded border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-200 mb-4">
          {error}
        </div>
      )}

      {result && (
        <div className="rounded border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200 mb-4 flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div>
              Synced <b>{result.merged}</b> player
              {result.merged === 1 ? "" : "s"} · wrote <b>{result.aliases_written || 0}</b>{" "}
              alias entries · re-pointed {result.totals?.moved || 0} FKs · deleted{" "}
              {result.totals?.deleted || 0} archival rows.
            </div>
            {result.errors?.length > 0 && (
              <div className="mt-1 text-rose-200">
                {result.errors.length} error
                {result.errors.length === 1 ? "" : "s"}: {result.errors[0].detail}
                {result.errors.length > 1 && ` (+ ${result.errors.length - 1} more)`}
              </div>
            )}
          </div>
        </div>
      )}

      {!preview && !error ? (
        <div className="text-text-secondary text-sm">Loading…</div>
      ) : !preview ? null : rows.length === 0 ? (
        <div className="rounded-2xl border border-border-default bg-bg-primary p-8 text-center">
          <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-text-primary mb-1">
            No archival players remaining
          </h3>
          <p className="text-sm text-text-secondary mb-2 max-w-md mx-auto">
            Every ingested player has already been mapped, or this{" "}
            {scope === "match" ? "match" : "competition"} has no ingested
            archival data.
          </p>
        </div>
      ) : (
        <>
          {/* Toolbar */}
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <label className="text-xs text-text-secondary flex items-center gap-2">
              Min balls in batch:
              <input
                type="number"
                min={0}
                value={minActivity}
                onChange={(e) => setMinActivity(Math.max(0, Number(e.target.value) || 0))}
                className="w-16 bg-bg-secondary border border-border-default rounded px-2 py-1 text-xs text-text-primary"
              />
              <span className="text-text-muted">
                ({filteredRows.length} of {rows.length} shown)
              </span>
            </label>
            <div className="flex-1" />
            <button
              onClick={clearAll}
              className="text-xs font-medium px-3 py-1.5 rounded-md bg-bg-secondary border border-border-default text-text-primary hover:bg-bg-main"
            >
              Clear all mappings
            </button>
            <div className="text-xs text-text-secondary">
              {mappedCount} of {filteredRows.length} mapped
            </div>
            <button
              disabled={saving || mappedCount === 0}
              onClick={onSave}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-md bg-button-primary text-white hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {saving ? "Saving…" : `Save ${mappedCount} mapping${mappedCount === 1 ? "" : "s"}`}
            </button>
          </div>

          {/* Groups */}
          {["auto_from_ci_id", "auto_from_alias", "high_confidence", "needs_review", "no_match"].map((tag) => {
            const rowsForTag = groups[tag] || [];
            if (rowsForTag.length === 0) return null;
            const meta = TAG_META[tag];
            const Icon = meta.icon;
            const collapsed = !!collapsedGroups[tag];
            return (
              <div key={tag} className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <button
                    onClick={() => toggleGroup(tag)}
                    className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full border ${meta.color}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {meta.label} · {rowsForTag.length}
                    <span className="text-text-muted">{collapsed ? "▸" : "▾"}</span>
                  </button>
                  <span className="text-xs text-text-secondary">{meta.hint}</span>
                  {(tag === "high_confidence" || tag === "needs_review") && (
                    <button
                      onClick={() => acceptGroup(tag)}
                      className="ml-2 text-[11px] font-medium px-2 py-0.5 rounded border border-button-primary/40 text-button-primary hover:bg-button-primary/10"
                    >
                      Accept top suggestions
                    </button>
                  )}
                </div>
                {!collapsed && (
                  <div className="rounded-lg border border-border-default overflow-hidden bg-bg-primary">
                    <div className="grid grid-cols-[3fr_2fr_3fr_1fr] text-[10px] uppercase tracking-wider text-text-secondary bg-bg-secondary/40 px-3 py-2 border-b border-border-default">
                      <div>Archival name · Team</div>
                      <div>Batting · Bowling</div>
                      <div>Map to canonical</div>
                      <div className="text-right">Action</div>
                    </div>
                    {rowsForTag.map((row) => (
                      <SyncRow
                        key={row.archival.id}
                        row={row}
                        mapping={mappings[row.archival.id] || ""}
                        onChange={(v) => setMap(row.archival.id, v)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

function SyncRow({ row, mapping, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [openSearch, setOpenSearch] = useState(false);
  // Path A — direct Cricinfo ID lookup. Operator types/pastes the id;
  // on Enter/blur we hit /api/coredata/players/?ci_player_id=<id> (exact
  // match, single row max per the DB partial-unique constraint) and
  // pre-select the canonical.
  const [ciId, setCiId] = useState("");
  const [ciLookupState, setCiLookupState] = useState("idle"); // idle | loading | matched | no_match | error
  const [ciMatchLabel, setCiMatchLabel] = useState("");
  // Track the display name of a custom pick (from CI lookup OR the
  // Search-other-player flow) so the dropdown shows the real name
  // instead of a generic "(searched player)" placeholder. Cleared
  // whenever the operator returns to a suggestion or skips.
  const [customPick, setCustomPick] = useState(null); // { id, label } | null
  const doCiLookup = async () => {
    const v = ciId.trim();
    if (!v) {
      setCiLookupState("idle");
      setCiMatchLabel("");
      return;
    }
    setCiLookupState("loading");
    try {
      // Direct api call — listPlayers only forwards its `search` opt +
      // URL-bar filters, not arbitrary ci_player_id.
      const r = await api.get("/api/coredata/players/", {
        params: { ci_player_id: v, page: 1 },
      });
      const first = r.data?.results?.[0];
      if (first) {
        const baseLabel = [first.first_name, first.middle_name, first.last_name]
          .filter(Boolean)
          .join(" ") || first.nick_name || "(no name)";
        const ciSuffix = first.ci_player_id ? ` · #${first.ci_player_id}` : "";
        const label = baseLabel + ciSuffix;
        onChange(first.id);
        setCustomPick({ id: first.id, label });
        setCiMatchLabel(baseLabel);
        setCiLookupState("matched");
      } else {
        setCiLookupState("no_match");
        setCiMatchLabel("");
      }
    } catch {
      setCiLookupState("error");
      setCiMatchLabel("");
    }
  };

  const options = useMemo(() => {
    const seen = new Set();
    const opts = [];
    for (const s of row.suggestions || []) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      const scoreBadge = s.score >= 2 ? "known" : s.score.toFixed(2);
      // Cricinfo id inline — the one guaranteed-unique per-canonical
      // identifier. Two "R Singh" suggestions become distinguishable.
      const ciPart = s.ci_player_id ? ` · #${s.ci_player_id}` : "";
      opts.push({
        id: s.id,
        label: `${s.name || s.full_name}${s.nationality ? ` · ${s.nationality}` : ""}${ciPart} · ${scoreBadge}`,
      });
    }
    return opts;
  }, [row.suggestions]);

  const doSearch = async () => {
    const query = q.trim();
    if (query.length < 2) return;
    setSearching(true);
    try {
      const r = await listPlayers(1, { search: query });
      setResults(r.data?.results || []);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const clear = () => {
    onChange("");
    setCustomPick(null);
    setCiLookupState("idle");
    setCiMatchLabel("");
    setCiId("");
  };
  const currentIsInOptions = options.some((o) => o.id === mapping);
  const showCustomSelected = mapping && !currentIsInOptions;
  // Label to show in the dropdown option for the custom pick — prefer
  // the tracked label from this session; fall back to a generic string
  // if we somehow have a mapping without a tracked label (shouldn't
  // happen in normal flow, but guard for safety).
  const customLabel = (customPick && customPick.id === mapping)
    ? customPick.label
    : "(searched player)";

  return (
    <div className="grid grid-cols-[3fr_2fr_3fr_1fr] items-start gap-2 px-3 py-3 border-b border-border-default last:border-b-0">
      <div>
        <div className="text-sm font-medium text-text-primary break-words flex items-center gap-2 flex-wrap">
          <span>{row.archival.name}</span>
          {row.archival.ci_player_id && (
            <span
              className="text-[10px] font-mono text-text-secondary"
              title={`Source Cricinfo ID ${row.archival.ci_player_id}`}
            >
              #{row.archival.ci_player_id}
            </span>
          )}
        </div>
        {row.archival.team_name && (
          <div className="mt-0.5 text-[10px] uppercase tracking-wider text-text-secondary">
            {row.archival.team_name}
          </div>
        )}
      </div>
      <div className="text-[11px] text-text-secondary font-mono leading-tight">
        {row.archival.balls_faced > 0 && (
          <div>{row.archival.runs}r / {row.archival.balls_faced}b</div>
        )}
        {row.archival.balls_bowled > 0 && (
          <div>
            {row.archival.wickets}w / {row.archival.balls_bowled}b bowled
          </div>
        )}
        {row.archival.balls_faced === 0 && row.archival.balls_bowled === 0 && (
          <div className="text-text-secondary/60">—</div>
        )}
      </div>
      <div>
        <select
          value={showCustomSelected ? "__custom__" : mapping}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "__custom__") return;
            // Operator moved off the custom pick — drop the tracked
            // label so a later custom pick doesn't inherit stale text.
            setCustomPick(null);
            onChange(v);
          }}
          className="w-full bg-bg-secondary border border-border-default rounded px-2 py-1.5 text-sm text-text-primary focus:outline-none focus:border-button-primary"
        >
          <option value="">(skip — leave archival)</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
          {showCustomSelected && (
            <option value="__custom__">{customLabel}</option>
          )}
        </select>
        {showCustomSelected && (
          <div className="mt-1 text-[11px] text-emerald-300 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Custom pick
          </div>
        )}
        {/* Cricinfo ID direct lookup — paste an id, hit Enter, we
            exact-match to the canonical (DB partial-unique guarantees
            0 or 1 result). Faster than typing a name when the operator
            has the id in hand. */}
        <div className="mt-1 flex items-center gap-1">
          <Fingerprint className="w-3 h-3 text-text-secondary shrink-0" />
          <input
            value={ciId}
            onChange={(e) => {
              setCiId(e.target.value);
              if (ciLookupState !== "idle") setCiLookupState("idle");
            }}
            onKeyDown={(e) => e.key === "Enter" && doCiLookup()}
            onBlur={() => { if (ciId.trim()) doCiLookup(); }}
            placeholder="Cricinfo ID"
            className="flex-1 min-w-0 bg-bg-secondary border border-border-default rounded px-2 py-0.5 text-[11px] text-text-primary font-mono focus:outline-none focus:border-button-primary"
          />
          {ciLookupState === "loading" && (
            <span className="text-[10px] text-text-secondary">…</span>
          )}
          {ciLookupState === "matched" && (
            <span className="text-[10px] text-emerald-300 truncate max-w-[10rem]" title={ciMatchLabel}>
              → {ciMatchLabel}
            </span>
          )}
          {ciLookupState === "no_match" && (
            <span className="text-[10px] text-rose-300">no match</span>
          )}
          {ciLookupState === "error" && (
            <span className="text-[10px] text-rose-300">error</span>
          )}
        </div>
        {!openSearch ? (
          <button
            type="button"
            onClick={() => setOpenSearch(true)}
            className="mt-1 text-[11px] text-button-primary hover:underline inline-flex items-center gap-1"
          >
            <Search className="w-3 h-3" /> Search other player
          </button>
        ) : (
          <div className="mt-1 space-y-1">
            <div className="flex gap-1">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doSearch()}
                placeholder="Type ≥ 2 chars…"
                className="flex-1 bg-bg-secondary border border-border-default rounded px-2 py-1 text-xs text-text-primary focus:outline-none focus:border-button-primary"
              />
              <button
                type="button"
                onClick={doSearch}
                className="text-xs px-2 py-1 rounded bg-button-primary text-white hover:brightness-110"
              >
                {searching ? "…" : "Go"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setOpenSearch(false);
                  setResults([]);
                  setQ("");
                }}
                className="text-xs px-2 py-1 rounded bg-bg-secondary border border-border-default text-text-secondary"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
            {results.length > 0 && (
              <ul className="rounded border border-border-default bg-bg-secondary max-h-40 overflow-y-auto">
                {results.map((p) => {
                  const nameLabel = [p.first_name, p.middle_name, p.last_name]
                    .filter(Boolean)
                    .join(" ");
                  const nickPart = p.nick_name ? ` — ${p.nick_name}` : "";
                  const baseLabel = (nameLabel || p.nick_name || "(no name)") + (nameLabel ? nickPart : "");
                  const ciPart = p.ci_player_id ? ` · #${p.ci_player_id}` : "";
                  const displayLabel = baseLabel + ciPart;
                  return (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => {
                          onChange(p.id);
                          setCustomPick({ id: p.id, label: displayLabel });
                          setOpenSearch(false);
                          setResults([]);
                          setQ("");
                        }}
                        className="w-full text-left px-2 py-1 text-xs hover:bg-bg-main text-text-primary"
                      >
                        {displayLabel}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
      <div className="text-right">
        {mapping && (
          <button
            type="button"
            onClick={clear}
            className="text-[11px] text-rose-300 hover:underline inline-flex items-center gap-1"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>
    </div>
  );
}
